import React, { useEffect, useState, useRef, useContext } from "react"; 
import { useStepContext } from "../../utils/StepContext.jsx";
import { useFormContext } from "../../utils/FormContext.jsx";
import { useAuth } from "../../utils/AuthContext/AuthContext.jsx";
import { AddressValueContext, AddressValueProvider } from "../../utils/AddressContext.jsx";
import { useMediaQuery } from 'react-responsive';
import config from '../../utils/config.js';

import MainCard from "../MainCard.jsx";

const NewSearchCard = ({ title = "What's the address?" }) => {
    const { handleSearch } = useStepContext(); 
    const { formData, handleAddressChange, handlePropertyDataChange } = useFormContext(); 
    const { searchAddress, setSearchAddress } = useContext(AddressValueContext);
    const isMobile = useMediaQuery({ query: '(max-width: 768px)' });

    const [isColorado, setIsColorado] = useState(true);
    const [addressError, setAddressError] = useState('');
    const autocompleteInputRef = useRef(null);

    useEffect(() => {
        const autocomplete = new google.maps.places.Autocomplete(autocompleteInputRef.current);

        autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace();
            const address = {
                street: '',
                city: '',
                state: '',
                zip: ''
            };
            for (const component of place.address_components) {
                const componentType = component.types[0];
                switch (componentType) {
                    case 'street_number': {
                        address.street = `${component.long_name} ${address.street}`;
                        break;
                    }
                    case 'route': {
                        address.street += component.short_name;
                        break;
                    }
                    case 'locality':
                        address.city = component.long_name;
                        break;
                    case 'administrative_area_level_1': {
                        address.state = component.short_name;
                        break;
                    }
                    case 'postal_code':
                        address.zip = component.short_name;
                        break;
                }
            }
            
            // Validate that we have a complete address
            const validation = validateAddress(address);
            if (!validation.isValid) {
                setAddressError(validation.error);
                return;
            }
            
            setAddressError(''); // Clear any previous errors
            handleAddressChange({
                target: {
                    name: 'address',
                    value: address
                }
            });
            setSearchAddress(Object.values(address).join(', '));
            checkIfColorado(address.state);
        });
    }, []);

    useEffect(() => {
        if (formData.data?.address) {
            setSearchAddress(prevSearchAddress => {
                const newSearchAddress = Object.values(formData.data.address).join(', ');
                checkIfColorado(formData.data.address.state);
                return newSearchAddress;
            });
        }
    }, [formData]);

    const checkIfColorado = (state) => {
        const coloradoKeywords = ['CO', 'co', 'colorado', 'Colorado'];
        const isInColorado = coloradoKeywords.includes(state);
        setIsColorado(isInColorado);
    };

    const validateAddress = (address) => {
        if (!address) {
            return { isValid: false, error: 'Please enter an address' };
        }
        
        if (!address.street || address.street.trim() === '') {
            return { isValid: false, error: 'Please enter a complete address including street number and name' };
        }
        
        if (!address.city || address.city.trim() === '') {
            return { isValid: false, error: 'Please enter a complete address including city' };
        }
        
        if (!address.state || address.state.trim() === '') {
            return { isValid: false, error: 'Please enter a complete address including state' };
        }
        
        if (!address.zip || address.zip.trim() === '') {
            return { isValid: false, error: 'Please enter a complete address including zip code' };
        }
        
        return { isValid: true, error: '' };
    };

    // OLD API CALL
    // async function searchPropertyDetailByAddressParts() {
    //     if (formData.address) {
    //         try {
    //             let search_options = {
    //                 method: 'POST',
    //                 headers: {
    //                     Accept: 'application/json',
    //                     'Content-Type': 'application/json',
    //                     'x-api-key': import.meta.env.VITE_REALESTATE_API_KEY
    //                 },
    //                 body: JSON.stringify({
    //                     address: formData.address.street + ', ' + formData.address.city + ' ' + formData.address.state + ' ' + formData.address.zip
    //                 })
    //             };
    //             const response = await fetch(`${config.apiBaseUrl}/propertydetail`, search_options);
    //             const data = await response.json();
    //             return data;
    //         } catch(e) {
    //             console.error(e);
    //         }
    //     } else {
    //         console.error('formData.data is undefined');
    //     }
    // }

    // NEW API CALL
    async function searchPropertyDetailByAddressParts() {
        if (!formData.address) {
            console.error('formData.address is undefined');
            return null;
        }

        // Validate that we have a complete address
        const validation = validateAddress(formData.address);
        if (!validation.isValid) {
            console.error('Incomplete address:', validation.error);
            setAddressError(validation.error);
            return null;
        }

        const fullAddress = `${formData.address.street}, ${formData.address.city}, ${formData.address.state} ${formData.address.zip}`;
        const encodedAddress = encodeURIComponent(fullAddress);
        
        try {
            const response = await fetch(`${config.apiBaseUrl}/propertydetail?address=${encodedAddress}`);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error fetching property details:', error);
            return null;
        }
    }

    const handleHomeSearch = async () => {
        // Check if we have a complete address
        const validation = validateAddress(formData.address);
        if (!validation.isValid) {
            setAddressError(validation.error);
            return;
        }
        
        if (formData.address) {
            if (!isColorado) {
                alert("Shishito is starting with the state of Colorado real estate contracts. Please enter a Colorado address.");
                return;
            }
            
            // Show animation immediately
            handleSearch();
            
            // Handle API call in the background
            const data = await searchPropertyDetailByAddressParts(formData.address); 
            if (data) {
                handlePropertyDataChange(data);
            } else {
                console.log("Failed to fetch property data");
            }
        } else {
            console.log("Could not validate address");
        }
    }; 

    return (
        <div className="w-full flex flex-col items-center">
            <div className={`${isMobile ? 'h-[600px]' : 'h-[750px]'} flex flex-col mt-8`}>
                {title && (
                <h1 className={`${isMobile ? 'text-[24px]' : 'text-[34px]' } font-medium w-full items-start text-black font-['Inter']`}>
                    {title}
                </h1>
                )}
                <div className="w-[350px] md:w-[750px] h-auto py-6 px-2 bg-white rounded-3xl shadow-[rgba(0,0,0,.5)_-2px_2px_10px_-5px] flex-col items-center gap-4 flex mt-3">
                    <div className={`${isMobile ? 'w-full px-[1px]' : 'w-full px-[2px]'} items-center gap-5 flex`}>
                        <div className="relative w-full">
                            <input 
                                ref={autocompleteInputRef}
                                type="text"
                                placeholder="Enter A Complete Colorado Address" 
                                className={`py-1 px-3 h-[35px] md:h-[50px] text-[16px] rounded-lg border ${addressError ? 'border-red-500' : 'border-slate-400'} w-full md:pr-[160px]`} 
                                value={searchAddress} 
                                name="address"
                                onChange={(event) => {
                                    setSearchAddress(event.target.value);
                                    setAddressError(''); // Clear error when user starts typing
                                    checkIfColorado(event.target.value);
                                }}
                                required
                            />    
                            <button 
                                className="absolute right-2 top-1/2 transform -translate-y-1/2 w-auto justify-center text-center text-white bg-shi-green text-[13px] md:text-[14px] font-normal font-['Inter'] py-1 md:py-2 px-2 md:px-4 rounded-md border hover:bg-green-950 flex" 
                                onClick={handleHomeSearch}
                            >
                                Search
                            </button>
                        </div>
                    </div>
                    {addressError && (
                        <div className="w-full px-2">
                            <p className="text-red-500 text-sm font-medium">{addressError}</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    ); 
}

export default NewSearchCard;